import React from 'react';
import { css } from '@emotion/react';
import IconPhone from '../../../images/svg/phone.svg';
import { PHONE_NUMBER } from '../../../constants';
import { useCtaProvider } from '../../../contexts/franchise/cta-provider';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import formatPhoneLink from '../../utils/format-phone-link';
import Link from '../../primitives/link';
import { colors } from '../../primitives/colors';
import Skeleton from '../../primitives/skeleton';
import { useLocator } from '../../../contexts/franchise/locator-provider';
import Container from '../../primitives/grid/container';
import Typography from '../../primitives/typography';

const styles = {
    linkButtonTypography: css({
        flexGrow: 1,
        textAlign: 'center',
        padding: '0 3px 7px 10px',
        whiteSpace: 'nowrap',
    }),
    linkButtonSkeleton: css({
        maxWidth: '120px',
        margin: '0 auto',
        height: '16px',
    }),
    ctaWrapper: css({ width: '100%' }),
    container: css({ height: '60px' }),
    phoneLink: css({
        display: 'flex',
        position: 'relative',
        paddingTop: '16px',
        paddingBottom: '16px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    }),
    phoneLinkContainer: css({
        display: 'flex',
        position: 'relative',
        alignItems: 'center',
    }),
    phoneLinkIcon: css({
        fill: 'currentColor',
        height: '14px',
    }),
    phoneLinkTypography: css({
        marginLeft: '10px',
        span: {
            lineHeight: 1,
        },
    }),
    openDrawerButton: css({
        display: 'flex',
        position: 'relative',
        paddingTop: '16px',
        paddingBottom: '16px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    }),
    ctaBottomPhoneNumbersContainer: css({
        height: '60px',
    }),
    ctaBottomPhoneNumbers: css({
        paddingLeft: '10px',
        paddingRight: '10px',
        display: 'flex',
        paddingTop: '8px',
        'a:nth-of-type(2):before': {
            content: '""',
            display: 'block',
            opacity: 0.4,
            width: '1px',
            height: '40px',
            background: 'currentColor',
            position: 'absolute',
        },
    }),
};

const LinkButton = ({ phone, loading, text }: {
    phone: string,
    loading: boolean,
    text: string
}): JSX.Element => (
    <Typography
        as={Link}
        fontSize="sm"
        fontWeight="medium"
        css={styles.linkButtonTypography}
        to={`tel:+${formatPhoneLink(phone)}`}
    >
        {text}
        {' '}
        <br />
        {(phone && !loading) ? (
            <span>
                {formatPhoneLink(phone)}
            </span>
        ) : (
            <Skeleton css={styles.linkButtonSkeleton} />
        )}
    </Typography>
);

const MobileCta = ({ inverse, setOpen }: { inverse?: boolean, setOpen: React.Dispatch<React.SetStateAction<boolean>> }): JSX.Element|null => {
    const {
        isMicrosite,
        franchise: micrositeFranchise,
        isLocalCityPage,
    } = useMicrosite();

    const { franchise: locatorFranchise, loading, isNationalCallCenter } = useLocator();
    const { hideNationalCallCenter, hideFranchiseLink, overridePhoneNumber, overridePhoneNumberDescriptor } = useCtaProvider();

    if (!!hideNationalCallCenter && !!hideFranchiseLink && !overridePhoneNumber) {
        return null;
    }

    const franchise = micrositeFranchise || locatorFranchise?.data;
    const phone = franchise?.mainPhone;

    const bottomContentHidden = isLocalCityPage || isMicrosite || !!hideNationalCallCenter || !!hideFranchiseLink;

    const phoneNumberLink: string = (() => {
        if (overridePhoneNumber) {
            return `tel:${formatPhoneLink(overridePhoneNumber)}`;
        }
        if (hideFranchiseLink) {
            return `tel:${formatPhoneLink(PHONE_NUMBER)}`;
        }
        if (phone) {
            return `tel:${formatPhoneLink(phone)}`;
        }
        return `tel:${formatPhoneLink(PHONE_NUMBER)}`;
    })();

    const displayPhoneNumber: string = (() => {
        if (overridePhoneNumber && overridePhoneNumberDescriptor) {
            return overridePhoneNumberDescriptor;
        }
        if (overridePhoneNumber) {
            return `Call: ${formatPhoneLink(overridePhoneNumber)}`;
        }
        if (hideFranchiseLink) {
            return 'National Call Center';
        }
        if (isMicrosite || isLocalCityPage) {
            return `Call: ${formatPhoneLink(phone)}`;
        }
        return 'Call for Service';
    })();

    const topText: string = (() => {
        if (isMicrosite || isLocalCityPage) {
            if (franchise?.tempOutOfService && !overridePhoneNumber) {
                return 'National Call Center';
            }
        }
        return '';
    })();

    return (
        <div
            css={styles.ctaWrapper}
            style={{
                color: inverse ? colors.primaryOrange : colors.shadesWhite,
                backgroundColor: inverse ? colors.shadesWhite : colors.primaryOrange,
            }}
        >
            <Container css={styles.container}>
                {bottomContentHidden && (
                    <Link
                        to={phoneNumberLink}
                        css={styles.phoneLink}
                    >
                        {topText && <p>{topText}</p>}
                        <div css={styles.phoneLinkContainer}>
                            <IconPhone css={styles.phoneLinkIcon} />
                            <Typography fontSize="xl" fontWeight="medium" css={styles.phoneLinkTypography}>
                                <span>{displayPhoneNumber}</span>
                            </Typography>
                        </div>
                    </Link>
                )}
                {!bottomContentHidden && (
                    <button
                        type="button"
                        css={styles.openDrawerButton}
                        onClick={() => setOpen(true)}
                    >
                        {topText && <p>{topText}</p>}
                        <div css={styles.phoneLinkContainer}>
                            <IconPhone css={styles.phoneLinkIcon} />
                            <Typography fontSize="xl" fontWeight="medium" css={styles.phoneLinkTypography}>
                                <span>{displayPhoneNumber}</span>
                            </Typography>
                        </div>
                    </button>
                )}
            </Container>
            {!bottomContentHidden && (
                <Container css={styles.ctaBottomPhoneNumbersContainer}>
                    <div css={styles.ctaBottomPhoneNumbers}>
                        {!hideFranchiseLink && !isLocalCityPage && !isNationalCallCenter && (
                            <LinkButton
                                phone={phone || ''}
                                loading={loading}
                                text="Your Local SERVPRO"
                            />
                        )}
                        {!hideNationalCallCenter && !hideFranchiseLink && (
                            <LinkButton
                                phone={PHONE_NUMBER}
                                loading={false}
                                text="National Call Center"
                            />
                        )}
                    </div>
                </Container>
            )}
        </div>
    );
};

export default MobileCta;
